import { frag, el } from './dom';
import { pricing, annualKr, tiers, type EncodingId, type Tier } from '../data/pricing';

export interface CalcInputs {
  tokensPerCall: Record<EncodingId, number>;
  users: number;
  callsPerUserPerDay: number;
  daysPerYear: number;
  o200kTier: Tier;
}
export interface CalcResults {
  p50k_base: number;
  cl100k_base: number;
  o200k_base: number;
}

export function computeAnnualForAll(input: CalcInputs): CalcResults {
  return {
    p50k_base: annualKr(input.tokensPerCall.p50k_base, input.users, input.callsPerUserPerDay, input.daysPerYear, pricing.p50k_base.usdPerMillion),
    cl100k_base: annualKr(input.tokensPerCall.cl100k_base, input.users, input.callsPerUserPerDay, input.daysPerYear, pricing.cl100k_base.usdPerMillion),
    o200k_base: annualKr(input.tokensPerCall.o200k_base, input.users, input.callsPerUserPerDay, input.daysPerYear, input.o200kTier.usdPerMillion)
  };
}

export interface CalculatorBlock {
  setTokens(tpc: Record<EncodingId, number>): void;
}

export function renderCalculator(root: HTMLElement, initialTokens: Record<EncodingId, number>): CalculatorBlock {
  let users = 1000, callsPerDay = 5, daysPerYear = 220;
  let activeTier: Tier = tiers[0];
  let tokens = initialTokens;

  root.classList.add('ow-calc');
  const tpl = `
    <h2 class="ow-calc-title">skaler opp</h2>
    <div class="ow-calc-inputs">
      <label>brukere<input type="number" min="1" data-key="users" value="1000"></label>
      <label>bruk per dag<input type="number" min="1" data-key="calls" value="5"></label>
      <label>dager per år<input type="number" min="1" max="365" data-key="days" value="220"></label>
    </div>
    <div class="ow-calc-tiers"></div>
    <div class="ow-calc-out">
      <div class="ow-calc-cell" data-enc="p50k_base"><div class="ow-calc-cell-label">p50k_base · text-davinci-003</div><div class="ow-calc-cell-num">–</div></div>
      <div class="ow-calc-cell" data-enc="cl100k_base"><div class="ow-calc-cell-label">cl100k_base · GPT-4 (8K)</div><div class="ow-calc-cell-num">–</div></div>
      <div class="ow-calc-cell" data-enc="o200k_base"><div class="ow-calc-cell-label">o200k_base · <span class="ow-calc-tier-name">GPT-5</span></div><div class="ow-calc-cell-num">–</div></div>
    </div>
    <p class="ow-calc-note">kr per år · samme tekst, gjentatt</p>
    <p class="ow-calc-disclosure">priser er hentet fra OpenAIs publiserte API-priser i mai 2026. eldre modeller (text-davinci-003, GPT-4 8K) er pensjonert; vi viser deres historiske priser slik de var i sin tid. valutakurs satt til 1 USD = 10,50 NOK.</p>
  `;
  root.replaceChildren(frag(tpl));

  const tiersWrap = root.querySelector<HTMLElement>('.ow-calc-tiers')!;
  tiers.forEach((t, i) => {
    if (i > 0) tiersWrap.appendChild(el('span', { class: 'ow-sep', text: '·', attrs: { 'aria-hidden': 'true' } }));
    const btn = el('button', {
      class: `ow-calc-tier${i === 0 ? ' on' : ''}`, text: t.name,
      attrs: { type: 'button', 'data-tier': t.name }
    });
    tiersWrap.appendChild(btn);
  });

  function recompute(): void {
    const r = computeAnnualForAll({ tokensPerCall: tokens, users, callsPerUserPerDay: callsPerDay, daysPerYear, o200kTier: activeTier });
    (['p50k_base', 'cl100k_base', 'o200k_base'] as const).forEach(enc => {
      const cell = root.querySelector(`.ow-calc-cell[data-enc="${enc}"] .ow-calc-cell-num`)!;
      const v = r[enc];
      cell.textContent = v < 1000
        ? `${v.toFixed(0)} kr`
        : `${(v / 1000).toFixed(1).replace('.', ',')} k kr`;
    });
    root.querySelector('.ow-calc-tier-name')!.textContent = activeTier.name;
  }

  root.querySelectorAll<HTMLInputElement>('input[type=number]').forEach(inp => {
    inp.addEventListener('input', () => {
      const v = Math.max(1, Number(inp.value || 1));
      if (inp.dataset.key === 'users') users = v;
      if (inp.dataset.key === 'calls') callsPerDay = v;
      if (inp.dataset.key === 'days') daysPerYear = v;
      recompute();
    });
  });
  root.querySelectorAll<HTMLButtonElement>('.ow-calc-tier').forEach(btn => {
    btn.addEventListener('click', () => {
      activeTier = tiers.find(t => t.name === btn.dataset.tier)!;
      root.querySelectorAll('.ow-calc-tier').forEach(b => b.classList.remove('on'));
      btn.classList.add('on');
      recompute();
    });
  });

  recompute();
  return { setTokens(t) { tokens = t; recompute(); } };
}
